import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  organizationConsentModeApi,
  RequestError,
  type TenantConsentMode,
} from '../api/client';

interface Props {
  children: ReactNode;
}

/**
 * 産業医（healthcare）画面のラッパー。
 * テナントのモードが `anonymous` の場合、BE 側で `/v1/healthcare/*` がすべて 403 を返すため、
 * API 呼び出し前に画面レベルで「この機能は個人特定モードで利用可能です」と案内する
 */
export default function HealthcareModeGate({ children }: Props) {
  const { t } = useTranslation();
  const { getToken } = useAuth();
  const [mode, setMode] = useState<TenantConsentMode | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    organizationConsentModeApi
      .getSelf(token)
      .then((res) => {
        if (cancelled) return;
        setMode(res.consent_mode);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // BE が未実装の場合は gate を効かせず通す（既存挙動を維持）
        if (err instanceof RequestError && err.status === 404) {
          setMode(null);
          return;
        }
        setError(err instanceof Error ? err.message : t('common.error'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getToken, t]);

  if (loading) {
    return <div className="loading-screen">{t('common.loading')}</div>;
  }

  if (error) {
    return (
      <div className="page healthcare-gate-page">
        <div className="admin-error" role="alert">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (mode === 'anonymous') {
    return (
      <div className="page healthcare-gate-page">
        <div className="admin-card healthcare-gate-blocked" role="status">
          <h1>{t('healthcare.modeGate.title')}</h1>
          <p>{t('healthcare.modeGate.body')}</p>
          <p className="admin-form-hint">{t('healthcare.modeGate.howTo')}</p>
          <p className="admin-back">
            <Link to="/">{t('common.back')}</Link>
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
